import React, { useEffect, useRef, useState } from 'react';

/**
 * AmbientLayer - "Living background" ambience canvas (SPACES_NEXT_LAYER_SPEC Arc 1, layers 3+4)
 *
 * One absolutely-positioned canvas meant to sit BEHIND the chat column content.
 * Parent provides the stacking context; this root uses a very low zIndex (0)
 * and expects siblings to be layered above it (position:relative + zIndex >= 1).
 *
 * Props:
 * - theme: 'rain' | 'cafe' | 'forest' | 'arcade' | 'airy'
 *   (same strings as pickAmbientTheme in utils/SoundManager.js so sight and sound agree;
 *    unknown values fall back to 'airy')
 * - accent: optional CSS hex color ('#rrggbb') to tint the background wash
 * - enabled: default true; false renders nothing and runs nothing
 * - field: optional, WHERE scene particles may live ({ box, from, lane, gain, bare };
 *   see THE FIELD below). Absent = the whole canvas, exactly as before.
 *
 * Layer 3 - sparse theme particles (density scales with area, hard cap 40, opacity <= 0.3):
 *   rain -> drifting droplet streaks · cafe -> warm dust motes · forest -> pulsing fireflies
 *   arcade -> blinking neon pixels · airy -> slow floating bubbles
 * Layer 4 - tap ripples: pointerdown anywhere over the canvas spawns a soft expanding
 *   ring that fades over ~1.2s (canvas itself is pointerEvents:none; taps are heard on
 *   window and hit-tested against the canvas bounds).
 *
 * Performance contract (hard requirement):
 * - Single rAF loop. The loop STOPS entirely (no ticking) when the tab is hidden,
 *   when IntersectionObserver reports the canvas offscreen, or when enabled=false.
 * - devicePixelRatio-aware, capped at 2. Particle + ripple objects are pooled at init;
 *   no per-frame allocations. Resize handled via debounced ResizeObserver.
 * - HARD gate: prefers-reduced-motion renders nothing at all (reacts to live changes).
 */

// ⚠️ POOL ALLOCATION SIZE, not just a clamp — the pool is built with exactly
// this many objects at mount (see below). targetCount must never exceed it or
// seedParticle indexes past the end and the whole layer dies on `p.color = …`.
// Raised 40 → 130 for scene atmospheres (SCENE_SYSTEM_SPEC §3); base themes
// still cap themselves at BASE_MAX_PARTICLES and are visually unchanged.
const MAX_PARTICLES = 130;
const BASE_MAX_PARTICLES = 40;
const MAX_RIPPLES = 5;
const RIPPLE_LIFE_MS = 1200;
const TAP_THROTTLE_MS = 150;
const RESIZE_DEBOUNCE_MS = 150;
const DT_CLAMP_S = 0.05; // ignore huge frame gaps (tab restore etc.)

// Per-theme look: particle colors (solid; alpha applied via ctx.globalAlpha),
// wash = very faint radial gradient tint on the wrapper (CSS, zero per-frame cost),
// ripple stroke color.
const THEME_STYLES = {
  rain: {
    colors: ['#9db8d6', '#7f9dc4', '#b6cbe4'],
    wash: 'rgba(90, 120, 165, 0.05)',
    ripple: '#a9c4e2',
  },
  cafe: {
    colors: ['#e6c896', '#d9b077', '#f0dcb4'],
    wash: 'rgba(200, 150, 80, 0.05)',
    ripple: '#e8cfa0',
  },
  forest: {
    colors: ['#c9e88a', '#a8d96a', '#e4f2b0'],
    wash: 'rgba(90, 150, 80, 0.05)',
    ripple: '#bfe287',
  },
  arcade: {
    colors: ['#5ef2f2', '#f25ef2', '#f2f25e', '#7a7af7'],
    wash: 'rgba(110, 70, 200, 0.05)',
    ripple: '#8be2f2',
  },
  airy: {
    colors: ['#cfe2f0', '#e2ecf5', '#bcd4e8'],
    wash: 'rgba(150, 175, 205, 0.04)',
    ripple: '#d5e5f2',
  },
};

// ── 🎭 SCENE ATMOSPHERES (SCENE_SYSTEM_SPEC_2026_08_07 §3.1) ────────────────
// A live /scene temporarily overrides the room's look. These are NOT the base
// themes above: a base theme is permanent wallpaper (hence alpha ≤0.3), while a
// scene is a temporary EVENT and is allowed to be louder.
//
// ⚠️⚠️ BUT NOT MUCH LOUDER, AND HERE IS WHY. This canvas is NOT behind the chat,
// despite what the docstring at the top of this file says. Its mount is a later
// DOM sibling of the page container and the chat columns flex has `z auto`, so
// all three paint in the root stacking context in DOM order — the canvas is ON
// TOP of the chat text. It only reads as a background because every alpha is
// tiny. So raising opacity does not make the room more atmospheric, it makes
// the chat harder to read. The "noticeable" in Nina's ask is delivered by the
// one-time full-screen sweep (fired from GameStepPage over document.body) plus
// particle CHARACTER and count — not by opacity. Keep sceneAlpha modest.
//
// `kind` selects a draw primitive; ten primitives cover all sixteen.
const SCENE_STYLES = {
  smoke:    { kind: 'puff',    colors: ['#9a9186', '#b0a599', '#7d7469'], wash: 'rgba(150, 140, 125, 0.10)', ripple: '#b0a599' },
  fire:     { kind: 'spark',   colors: ['#ff9d3c', '#ff6b2c', '#ffd07a'], wash: 'rgba(255, 120, 40, 0.10)',  ripple: '#ff9d3c' },
  embers:   { kind: 'spark',   colors: ['#ff8a3c', '#ffb562', '#e8552a'], wash: 'rgba(255, 110, 50, 0.08)',  ripple: '#ffb562' },
  rain:     { kind: 'streak',  colors: ['#9db8d6', '#7f9dc4', '#b6cbe4'], wash: 'rgba(90, 120, 165, 0.10)',  ripple: '#a9c4e2' },
  storm:    { kind: 'streak',  colors: ['#8fa8c8', '#6b86ad', '#aabfd8'], wash: 'rgba(50, 70, 115, 0.14)',   ripple: '#8fa8c8' },
  snow:     { kind: 'flake',   colors: ['#ffffff', '#e8f2ff', '#cfe0f5'], wash: 'rgba(190, 215, 240, 0.10)', ripple: '#e8f2ff' },
  fog:      { kind: 'puff',    colors: ['#b8bcc2', '#cdd1d6', '#a3a8af'], wash: 'rgba(180, 185, 195, 0.13)', ripple: '#cdd1d6' },
  dust:     { kind: 'mote',    colors: ['#c8b48c', '#ddc9a0', '#a8946f'], wash: 'rgba(190, 165, 115, 0.09)', ripple: '#ddc9a0' },
  bloom:    { kind: 'flake',   colors: ['#ffc0d4', '#ffd9e6', '#f5a8c4'], wash: 'rgba(255, 175, 205, 0.09)', ripple: '#ffd9e6' },
  leaves:   { kind: 'flake',   colors: ['#d68a3c', '#c46a2a', '#e8b062'], wash: 'rgba(200, 115, 45, 0.09)',  ripple: '#d68a3c' },
  bubbles:  { kind: 'bubble',  colors: ['#8fd8e8', '#b0e6f2', '#6ec4d8'], wash: 'rgba(80, 180, 210, 0.11)',  ripple: '#b0e6f2' },
  stars:    { kind: 'twinkle', colors: ['#ffffff', '#dfe6ff', '#b9c6ff'], wash: 'rgba(90, 105, 180, 0.11)',  ripple: '#dfe6ff' },
  confetti: { kind: 'flake',   colors: ['#ff5e5e', '#5ef2a8', '#5eb8f2', '#f2d65e', '#c95ef2'], wash: 'rgba(255, 170, 90, 0.09)', ripple: '#f2d65e' },
  dark:     { kind: 'none',    colors: ['#000000'], wash: 'rgba(0, 0, 0, 0.30)', ripple: '#555555' },
  heat:     { kind: 'shimmer', colors: ['#ffb877', '#ffd0a0', '#ff9c55'], wash: 'rgba(255, 150, 70, 0.10)',  ripple: '#ffd0a0' },
  glitch:   { kind: 'glitch',  colors: ['#5ef2f2', '#f25ef2', '#f2f25e'], wash: 'rgba(120, 60, 200, 0.10)',  ripple: '#5ef2f2' },
  // 🪲 09-19 (Nina: fireflies on clear nights in the sunroom, greenhouse and
  // porch). The base 'forest' theme already had a firefly, but it THROBS on a
  // sine; a real one goes dark for seconds between flashes, and a constant
  // pulse reads as dust. Two plain fills (halo + core), never a canvas blur.
  fireflies: { kind: 'firefly', colors: ['#d9f26b', '#f4ffb0', '#c9ef6a'], wash: 'rgba(120, 150, 40, 0.05)', ripple: '#d9f26b' },
  // 🎆 09-14: bursts are their own layer (like the shooters); the base
  // particles for this kind are faint drifting sparks between booms.
  fireworks: { kind: 'burst', colors: ['#ffd07a', '#ff5e5e', '#5ef2a8', '#5eb8f2', '#c95ef2', '#ffffff'], wash: 'rgba(18, 22, 55, 0.15)', ripple: '#ffd07a' },
};

// Scene intensity → particle-count multiplier and alpha multiplier.
const SCENE_INTENSITY = {
  low:  { count: 0.55, alpha: 0.75 },
  med:  { count: 1.0,  alpha: 1.0 },
  high: { count: 1.6,  alpha: 1.25 },
};

function hexToRgba(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  /* eslint-disable no-bitwise */
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  /* eslint-enable no-bitwise */
  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
}

function makeParticle() {
  return {
    x: 0, y: 0,       // position (CSS px)
    vx: 0, vy: 0,     // velocity (CSS px / s)
    size: 1,          // radius or streak length depending on theme
    baseAlpha: 0.2,   // per-particle max alpha (always <= 0.3)
    phase: 0,         // pulse/wobble phase (rad)
    pulseHz: 0.5,     // pulse speed
    color: '#ffffff',
    blinkOn: 1,       // arcade: 1 visible / low when blinked off
    blinkAt: 0,       // arcade: next blink toggle time (s, loop clock)
  };
}

function seedParticle(p, themeKey, style, w, h) {
  p.color = style.colors[(Math.random() * style.colors.length) | 0]; // eslint-disable-line no-bitwise
  p.x = Math.random() * w;
  p.y = Math.random() * h;
  p.phase = Math.random() * Math.PI * 2;
  p.blinkOn = 1;
  p.blinkAt = 0;
  switch (themeKey) {
    case 'rain':
      p.vx = -4 - Math.random() * 6;
      p.vy = 42 + Math.random() * 34;
      p.size = 7 + Math.random() * 9;       // streak length
      p.baseAlpha = 0.10 + Math.random() * 0.12;
      break;
    case 'cafe':
      p.vx = 2 + Math.random() * 5;
      p.vy = -2 - Math.random() * 4;
      p.size = 1 + Math.random() * 1.6;
      p.baseAlpha = 0.10 + Math.random() * 0.14;
      p.pulseHz = 0.15 + Math.random() * 0.2;
      break;
    case 'forest':
      p.vx = (Math.random() - 0.5) * 10;
      p.vy = (Math.random() - 0.5) * 8;
      p.size = 1.4 + Math.random() * 1.8;
      p.baseAlpha = 0.14 + Math.random() * 0.16; // pulses down to ~0
      p.pulseHz = 0.25 + Math.random() * 0.5;
      break;
    case 'arcade':
      p.vx = (Math.random() - 0.5) * 6;
      p.vy = (Math.random() - 0.5) * 6;
      p.size = 1.5 + Math.random() * 1.5;    // square half-size-ish
      p.baseAlpha = 0.12 + Math.random() * 0.14;
      p.blinkAt = 1 + Math.random() * 6;
      break;
    case 'airy':
    default:
      p.vx = (Math.random() - 0.5) * 6;
      p.vy = -3 - Math.random() * 6;
      p.size = 2 + Math.random() * 3.5;
      p.baseAlpha = 0.08 + Math.random() * 0.12;
      p.pulseHz = 0.1 + Math.random() * 0.15;
      break;
  }
}

function targetCount(w, h) {
  // ~18 particles at 1920x1080, scaled by area; clamped 8..40.
  const scaled = Math.round((w * h) / (1920 * 1080) * 18);
  return Math.max(8, Math.min(BASE_MAX_PARTICLES, scaled));
}

// 🎭 Seed one particle for a scene atmosphere. Mirrors seedParticle's contract
// (mutates in place, no allocation) but per PRIMITIVE rather than per theme, so
// sixteen atmospheres share ten behaviors.
function seedScene(p, kind, style, w, h, alphaMul) {
  p.color = style.colors[(Math.random() * style.colors.length) | 0]; // eslint-disable-line no-bitwise
  p.x = Math.random() * w;
  p.y = Math.random() * h;
  p.phase = Math.random() * Math.PI * 2;
  p.blinkOn = 1;
  p.blinkAt = 0;
  p.spin = 0;
  switch (kind) {
    case 'puff': // smoke, fog — big soft masses drifting up and sideways
      p.vx = 5 + Math.random() * 14;
      p.vy = -6 - Math.random() * 14;
      p.size = 26 + Math.random() * 54;
      p.baseAlpha = (0.035 + Math.random() * 0.05) * alphaMul;
      p.pulseHz = 0.05 + Math.random() * 0.1;
      break;
    case 'spark': // fire, embers — small, fast, upward, flickering
      p.vx = (Math.random() - 0.5) * 22;
      p.vy = -34 - Math.random() * 52;
      p.size = 1 + Math.random() * 2.4;
      p.baseAlpha = (0.3 + Math.random() * 0.4) * alphaMul;
      p.pulseHz = 1.6 + Math.random() * 2.6;
      break;
    case 'streak': // rain, storm
      p.vx = -6 - Math.random() * 12;
      p.vy = 150 + Math.random() * 190;
      p.size = 11 + Math.random() * 16;
      p.baseAlpha = (0.14 + Math.random() * 0.16) * alphaMul;
      break;
    case 'burst': // fireworks — faint ember drift between the bursts
      p.vx = (Math.random() - 0.5) * 8;
      p.vy = -5 - Math.random() * 10;
      p.size = 0.9 + Math.random() * 1.4;
      p.baseAlpha = (0.12 + Math.random() * 0.14) * alphaMul;
      p.pulseHz = 0.8 + Math.random() * 1.4;
      break;
    case 'flake': // snow, petals, leaves, confetti — drift down with sway/spin
      p.vx = (Math.random() - 0.5) * 22;
      p.vy = 20 + Math.random() * 42;
      p.size = 2 + Math.random() * 4;
      p.baseAlpha = (0.22 + Math.random() * 0.24) * alphaMul;
      p.pulseHz = 0.3 + Math.random() * 0.7;
      p.spin = (Math.random() - 0.5) * 5;
      break;
    case 'mote': // dust
      p.vx = (Math.random() - 0.5) * 9;
      p.vy = -1 - Math.random() * 5;
      p.size = 0.8 + Math.random() * 1.7;
      p.baseAlpha = (0.16 + Math.random() * 0.2) * alphaMul;
      p.pulseHz = 0.12 + Math.random() * 0.25;
      break;
    case 'bubble':
      p.vx = (Math.random() - 0.5) * 9;
      p.vy = -14 - Math.random() * 26;
      p.size = 2.5 + Math.random() * 6;
      p.baseAlpha = (0.16 + Math.random() * 0.2) * alphaMul;
      p.pulseHz = 0.2 + Math.random() * 0.4;
      break;
    case 'firefly': { // slow wander, a flash every 3-6s, dark in between
      const dir = Math.random() * Math.PI * 2;
      const sp = 5 + Math.random() * 11;
      p.vx = Math.cos(dir) * sp;
      p.vy = Math.sin(dir) * sp * 0.6;
      p.size = 1.2 + Math.random() * 1.1;
      p.baseAlpha = (0.5 + Math.random() * 0.35) * alphaMul;
      p.pulseHz = 0.17 + Math.random() * 0.16;       // one flash per cycle
      p.spin = (Math.random() - 0.5) * 0.9;          // turn rate, rad/s
      break;
    }
    case 'twinkle': // stars — near-static, slow bright pulse
      p.vx = (Math.random() - 0.5) * 1.6;
      p.vy = (Math.random() - 0.5) * 1.6;
      p.size = 0.9 + Math.random() * 1.7;
      p.baseAlpha = (0.3 + Math.random() * 0.45) * alphaMul;
      p.pulseHz = 0.2 + Math.random() * 0.7;
      break;
    case 'shimmer': // heat — vertical bands wobbling in place
      p.vx = (Math.random() - 0.5) * 5;
      p.vy = -3 - Math.random() * 7;
      p.size = 12 + Math.random() * 26;   // band height
      p.baseAlpha = (0.05 + Math.random() * 0.07) * alphaMul;
      p.pulseHz = 0.7 + Math.random() * 1.4;
      break;
    case 'glitch': // digital tearing — rectangles that jump rather than drift
      p.vx = 0;
      p.vy = 0;
      p.size = 5 + Math.random() * 34;    // block width
      p.baseAlpha = (0.14 + Math.random() * 0.22) * alphaMul;
      p.blinkAt = Math.random() * 1.2;
      break;
    default: // 'none' (dark) — no particles at all, the wash does the work
      p.baseAlpha = 0;
      p.vx = 0;
      p.vy = 0;
      p.size = 0;
      break;
  }
}

// ── 🔍🪧 THE FIELD (09-19): WHERE particles may live ─────────────────────────
// Two callers, one mechanism, default OFF (no `field` prop = every line below
// is skipped and the layer is byte-for-byte what it was):
//  · the PEEK mounts this layer inside the picture and hands it a `box` (the
//    part of the painting the particles travel through) and a `from` (where
//    they are born): bubbles rise off the tub foam, embers off the hearth.
//    Both are [x0, y0, x1, y1] in % of the canvas.
//  · the ROOM hands it a `lane` { x0, x1 } in px: the chat column. Particles
//    are seeded in the page MARGINS and fade to nothing over the lane, so a
//    louder look never drifts across a message. Same canvas, same count.
// `gain` scales alpha (the peek has no chat text under it, so it may be
// louder); `bare` drops the wash tint + tap ripples (a tint over a painting is
// fog, and a peek click belongs to the glass).
const LANE_FADE_PX = 48;
function fieldBox(arr, w, h) {
  if (!Array.isArray(arr) || arr.length !== 4 || !arr.every((n) => Number.isFinite(Number(n)))) return null;
  const x0 = (Math.min(arr[0], arr[2]) / 100) * w; const x1 = (Math.max(arr[0], arr[2]) / 100) * w;
  const y0 = (Math.min(arr[1], arr[3]) / 100) * h; const y1 = (Math.max(arr[1], arr[3]) / 100) * h;
  return x1 - x0 >= 4 && y1 - y0 >= 4 ? { x0, y0, x1, y1 } : null;
}
function fieldLane(lane, w) {
  if (!lane || !Number.isFinite(Number(lane.x0)) || !Number.isFinite(Number(lane.x1))) return null;
  const x0 = Math.max(0, Math.min(lane.x0, lane.x1)); const x1 = Math.min(w, Math.max(lane.x0, lane.x1));
  return x1 > x0 ? { x0, x1 } : null;
}
/** A random x in the margins either side of the lane, weighted by their widths. */
function marginX(lane, w) {
  const left = Math.max(0, lane.x0); const right = Math.max(0, w - lane.x1);
  if (left + right <= 0) return Math.random() * w;
  return Math.random() * (left + right) < left ? Math.random() * left : lane.x1 + Math.random() * right;
}
function placeIn(p, b) { p.x = b.x0 + Math.random() * (b.x1 - b.x0); p.y = b.y0 + Math.random() * (b.y1 - b.y0); }

// Scene particle budget. ⚠️ Phones sit at the FLOOR of the base formula (a
// 390x844 viewport computes 3 → clamped to 8), so a multiplier alone would do
// literally nothing on a phone — the floor has to move too, and it moves less
// than desktop's does because a phone is a small battery holding a small pane.
function sceneCount(w, h, kind, mul, isPhone) {
  if (kind === 'none') return 0;
  const dense = kind === 'streak' || kind === 'spark' || kind === 'flake';
  const nominal = dense ? 52 : 26;
  const scaled = Math.round((w * h) / (1920 * 1080) * nominal * mul);
  const floor = isPhone ? Math.round(12 * mul) : Math.round(22 * mul);
  const ceil = isPhone ? 42 : MAX_PARTICLES;
  return Math.max(Math.min(floor, ceil), Math.min(ceil, scaled));
}

export default function AmbientLayer({ theme, accent = null, enabled = true, scene = null, field = null }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  // 🔍🪧 the field rides a ref for the same reason the scene does (see below):
  // the loop reads it per frame, a change re-seeds in place, nothing remounts.
  const fieldRef = useRef(null);

  // 🎭 Scene override plumbing (SCENE_SYSTEM_SPEC §3.3).
  //
  // ⚠️ THE WHOLE POINT OF THE REF: the main effect's deps are [themeKey, active],
  // and changing themeKey destroys and rebuilds EVERYTHING — all particles, both
  // observers, both listeners, every live ripple, and the `clock` that drives
  // pulses and blinks. Routing a scene through the deps would therefore make
  // every scene start and end with a visible hard cut. Instead the running loop
  // dereferences this ref once per frame, and a separate effect re-seeds the
  // pool in place when the scene changes. Nothing remounts.
  const sceneRef = useRef(null);
  const reseedRef = useRef(null);   // assigned by the main effect; re-seeds in place
  const isPhoneRef = useRef(false);

  useEffect(() => {
    const check = () => { isPhoneRef.current = typeof window !== 'undefined' && window.innerWidth <= 600; };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const sceneKey = scene?.atmosphere || null;
  const sceneIntensity = scene?.intensity || 'med';
  const sceneStreaks = !!(scene && scene.streaks); // 🌠 09-14: meteor/comet events
  useEffect(() => {
    const style = sceneKey ? SCENE_STYLES[sceneKey] : null;
    // Unknown atmosphere → drop to no override rather than guessing (the
    // whitelist-or-drop posture the backend uses for the same data).
    sceneRef.current = style
      ? { key: sceneKey, style, kind: style.kind, shooters: sceneStreaks, ...(SCENE_INTENSITY[sceneIntensity] || SCENE_INTENSITY.med) }
      : null;
    if (reseedRef.current) reseedRef.current();
  }, [sceneKey, sceneIntensity, sceneStreaks]);

  const fieldKey = field ? JSON.stringify([field.box || null, field.from || null, field.lane ? [Math.round(field.lane.x0), Math.round(field.lane.x1)] : null, field.gain || 1, !!field.bare]) : '';
  useEffect(() => {
    fieldRef.current = field || null;
    if (reseedRef.current) reseedRef.current();
  }, [fieldKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [reducedMotion, setReducedMotion] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ));

  // React to live prefers-reduced-motion changes.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReducedMotion(e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Older Safari fallback
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const themeKey = THEME_STYLES[theme] ? theme : 'airy';
  const active = enabled && !reducedMotion;

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const style = THEME_STYLES[themeKey];

    // ---- pooled state (allocated once per mount) ----
    const particles = [];
    for (let i = 0; i < MAX_PARTICLES; i++) particles.push(makeParticle());
    let particleCount = 0;

    const ripples = [];
    for (let i = 0; i < MAX_RIPPLES; i++) ripples.push({ x: 0, y: 0, born: -1 });

    let cssW = 0;
    let cssH = 0;
    let rafId = 0;
    let running = false;
    let lastTs = 0;
    let clock = 0; // seconds, only advances while running (used for pulses/blinks)
    let restAccum = 0; // dt banked between resting-cap frames (see tick)
    let lastTapAt = 0;
    let pageVisible = typeof document === 'undefined' ? true : !document.hidden;
    let onScreen = true; // until the observer reports otherwise
    let disposed = false;

    // Seed the pool for whatever look is current — base theme or scene override.
    // ⚠️ Must be called by anything that changes the field (resize AND scene
    // change): the ResizeObserver already re-seeds on mobile address-bar
    // show/hide and orientation change, so a scene applied as a one-time
    // mutation after seeding would silently revert. The scene has to be
    // readable AT seed time, which is why it lives in a ref.
    function seedAll() {
      const sc = sceneRef.current;
      if (sc) {
        particleCount = sceneCount(cssW, cssH, sc.kind, sc.count, isPhoneRef.current);
        for (let i = 0; i < particleCount; i++) seedScene(particles[i], sc.kind, sc.style, cssW, cssH, sc.alpha);
        // 🔍🪧 a field moves the seeds: into the painting's box (scattered
        // through the whole travel region so the first frame is already
        // mid-flight, not a burst), or out of the chat lane.
        const f = fieldRef.current;
        const box = f ? fieldBox(f.box, cssW, cssH) : null;
        const lane = f && !box ? fieldLane(f.lane, cssW) : null;
        if (box) for (let i = 0; i < particleCount; i++) placeIn(particles[i], box);
        else if (lane) for (let i = 0; i < particleCount; i++) particles[i].x = marginX(lane, cssW);
      } else {
        particleCount = targetCount(cssW, cssH);
        for (let i = 0; i < particleCount; i++) seedParticle(particles[i], themeKey, style, cssW, cssH);
      }
    }
    reseedRef.current = () => { if (!disposed) seedAll(); };

    function sizeCanvas() {
      const rect = wrap.getBoundingClientRect();
      cssW = Math.max(1, rect.width);
      cssH = Math.max(1, rect.height);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedAll();
    }

    // 🌠 One shooting star at a time (09-14 sky batch): lives only while a
    // scene with shooters is current, spawns ~every 7s, dies in under a
    // second. Reduced-motion never reaches here — the layer renders nothing
    // at all under it (the hard gate above).
    let shooter = null;
    // 🎆 Fireworks bursts (09-14): max two live, each a rocket that rises
    // then blooms into a ring of falling sparks. Same lifecycle discipline.
    let bursts = [];

    function drawFrame(dt) {
      ctx.clearRect(0, 0, cssW, cssH);
      const sc = sceneRef.current;
      // 🔍🪧 resolved once per frame, never per particle
      const fld = sc ? fieldRef.current : null;
      const box = fld ? fieldBox(fld.box, cssW, cssH) : null;
      const from = box ? fieldBox(fld.from, cssW, cssH) : null;
      const lane = fld && !box ? fieldLane(fld.lane, cssW) : null;
      const gain = fld && Number(fld.gain) > 0 ? Math.min(2.5, Number(fld.gain)) : 1;

      // Layer 3: particles
      for (let i = 0; i < particleCount; i++) {
        const p = particles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.phase += p.pulseHz * Math.PI * 2 * dt;

        // wrap with a margin so nothing pops at the edges. Scene particles can
        // be far bigger than base ones (a smoke puff is up to 80px), so the
        // margin follows the particle instead of a fixed 20.
        const m = sc ? Math.max(20, p.size + 10) : 20;
        let fieldMul = 1;
        if (sc && box) {
          // 🔍 inside a painting: wanderers turn back at the edge, everything
          // else is reborn (fallers at the top, risers where they come from),
          // and every particle fades toward the box edge so nothing pops.
          const out = p.x < box.x0 || p.x > box.x1 || p.y < box.y0 || p.y > box.y1;
          if (out && (sc.kind === 'firefly' || sc.kind === 'twinkle')) {
            if (p.x < box.x0 || p.x > box.x1) p.vx = -p.vx;
            if (p.y < box.y0 || p.y > box.y1) p.vy = -p.vy;
            p.x = Math.max(box.x0, Math.min(box.x1, p.x));
            p.y = Math.max(box.y0, Math.min(box.y1, p.y));
          } else if (out) {
            if (p.vy > 0) { p.x = box.x0 + Math.random() * (box.x1 - box.x0); p.y = box.y0; } else placeIn(p, from || box);
          }
          const fx = Math.max(6, (box.x1 - box.x0) * 0.12); const fy = Math.max(6, (box.y1 - box.y0) * 0.15);
          fieldMul = Math.max(0, Math.min(1, (p.x - box.x0) / fx, (box.x1 - p.x) / fx, (p.y - box.y0) / fy, (box.y1 - p.y) / fy));
        } else {
          if (p.x < -m) p.x += cssW + m * 2;
          else if (p.x > cssW + m) p.x -= cssW + m * 2;
          const wrappedY = p.y < -m || p.y > cssH + m;
          if (p.y < -m) p.y += cssH + m * 2;
          else if (p.y > cssH + m) p.y -= cssH + m * 2;
          if (sc && lane) {
            // 🪧 the chat lane: a vertical wrap re-enters in a margin, and
            // anything that drifts over the column fades to nothing first
            if (wrappedY) p.x = marginX(lane, cssW);
            if (p.x > lane.x0 - LANE_FADE_PX && p.x < lane.x1 + LANE_FADE_PX) {
              fieldMul = p.x <= lane.x0 ? (lane.x0 - p.x) / LANE_FADE_PX : p.x >= lane.x1 ? (p.x - lane.x1) / LANE_FADE_PX : 0;
            }
          }
        }

        let alpha = p.baseAlpha * fieldMul * gain;
        ctx.fillStyle = p.color;

        // 🎭 Scene primitives take precedence over the base theme's look.
        if (sc) {
          const k = sc.kind;
          if (k === 'none') {
            // nothing to draw — `dark` is carried entirely by the wash
          } else if (k === 'puff') {
            ctx.globalAlpha = alpha * (0.7 + 0.3 * Math.sin(p.phase));
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else if (k === 'spark') {
            ctx.globalAlpha = alpha * (0.45 + 0.55 * Math.abs(Math.sin(p.phase)));
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else if (k === 'burst') {
            ctx.globalAlpha = alpha * (0.5 + 0.5 * Math.abs(Math.sin(p.phase)));
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else if (k === 'streak') {
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = p.color;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x + p.vx * 0.09, p.y + p.size);
            ctx.stroke();
          } else if (k === 'flake') {
            ctx.globalAlpha = alpha;
            const sway = Math.sin(p.phase) * 9;
            const s = p.size;
            if (p.spin) {
              // petals/leaves/confetti tumble; snow (spin ~0) just sways
              ctx.save();
              ctx.translate(p.x + sway, p.y);
              ctx.rotate(p.phase * p.spin * 0.35);
              ctx.fillRect(-s, -s * 0.45, s * 2, s * 0.9);
              ctx.restore();
            } else {
              ctx.beginPath();
              ctx.arc(p.x + sway, p.y, s, 0, Math.PI * 2);
              ctx.fill();
            }
          } else if (k === 'mote') {
            ctx.globalAlpha = alpha * (0.6 + 0.4 * Math.sin(p.phase));
            ctx.beginPath();
            ctx.arc(p.x, p.y + Math.sin(p.phase) * 3, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else if (k === 'bubble') {
            ctx.globalAlpha = alpha * (0.7 + 0.3 * Math.sin(p.phase));
            ctx.strokeStyle = p.color;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.arc(p.x + Math.sin(p.phase) * 5, p.y, p.size, 0, Math.PI * 2);
            ctx.stroke();
          } else if (k === 'firefly') {
            // wander: the heading turns slowly, and now and then changes its mind
            const turn = p.spin * dt; const cs = Math.cos(turn); const sn = Math.sin(turn);
            const vx = p.vx * cs - p.vy * sn; p.vy = p.vx * sn + p.vy * cs; p.vx = vx;
            if (Math.random() < 0.004) p.spin = (Math.random() - 0.5) * 0.9;
            // lit for roughly a third of the cycle, fully dark for the rest
            const s = Math.sin(p.phase);
            const lit = s > 0.45 ? ((s - 0.45) / 0.55) ** 1.5 : 0;
            if (lit > 0.01) {
              ctx.globalAlpha = Math.min(1, alpha * lit * 0.22);
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size * 3.4, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalAlpha = Math.min(1, alpha * lit);
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
            }
          } else if (k === 'twinkle') {
            ctx.globalAlpha = alpha * (0.25 + 0.75 * Math.abs(Math.sin(p.phase)));
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else if (k === 'shimmer') {
            // horizontal heat bands that wobble in place
            ctx.globalAlpha = alpha * (0.5 + 0.5 * Math.sin(p.phase));
            ctx.fillRect(0, p.y + Math.sin(p.phase) * 5, cssW, p.size * 0.35);
          } else if (k === 'glitch') {
            // jump to a new position on a stutter rather than drifting
            if (clock >= p.blinkAt) {
              p.x = Math.random() * cssW;
              p.y = Math.random() * cssH;
              p.size = 5 + Math.random() * 34;
              p.blinkOn = Math.random() > 0.35 ? 1 : 0;
              p.blinkAt = clock + 0.05 + Math.random() * 0.5;
            }
            ctx.globalAlpha = alpha * p.blinkOn;
            ctx.fillRect(p.x, p.y, p.size * 2.6, 2 + Math.random() * 3);
          }
          continue;
        }

        if (themeKey === 'rain') {
          ctx.globalAlpha = alpha;
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + p.vx * 0.12, p.y + p.size);
          ctx.stroke();
        } else if (themeKey === 'forest') {
          // firefly: soft sine pulse, dips to near-invisible
          alpha *= 0.5 + 0.5 * Math.sin(p.phase);
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        } else if (themeKey === 'arcade') {
          if (clock >= p.blinkAt) {
            p.blinkOn = p.blinkOn === 1 ? 0.25 : 1;
            p.blinkAt = clock + (p.blinkOn === 1 ? 1 + Math.random() * 6 : 0.12 + Math.random() * 0.2);
          }
          ctx.globalAlpha = alpha * p.blinkOn;
          ctx.fillRect(p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
        } else if (themeKey === 'airy') {
          // bubble: faint stroked circle with gentle sway
          ctx.globalAlpha = alpha * (0.75 + 0.25 * Math.sin(p.phase));
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x + Math.sin(p.phase) * 4, p.y, p.size, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          // cafe dust motes: soft dots with slow shimmer
          ctx.globalAlpha = alpha * (0.7 + 0.3 * Math.sin(p.phase));
          ctx.beginPath();
          ctx.arc(p.x, p.y + Math.sin(p.phase) * 2, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // 🌠 Layer 3.5: the shooting star (meteor/comet events only)
      if (sc && sc.shooters) {
        if (!shooter && Math.random() < dt / 7) {
          const fromLeft = Math.random() < 0.5;
          shooter = {
            x: fromLeft ? -10 : cssW * (0.2 + Math.random() * 0.8),
            y: Math.random() * cssH * 0.4,
            vx: (fromLeft ? 1 : -1) * (260 + Math.random() * 220),
            vy: 90 + Math.random() * 150,
            life: 0.55 + Math.random() * 0.35,
            age: 0,
          };
        }
        if (shooter) {
          shooter.age += dt;
          shooter.x += shooter.vx * dt;
          shooter.y += shooter.vy * dt;
          const t = shooter.age / shooter.life;
          if (t >= 1 || shooter.x < -80 || shooter.x > cssW + 80 || shooter.y > cssH + 40) {
            shooter = null;
          } else {
            const fade = Math.sin(Math.PI * Math.min(1, t)); // in and out
            ctx.globalAlpha = 0.5 * fade;
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.moveTo(shooter.x, shooter.y);
            ctx.lineTo(shooter.x - shooter.vx * 0.12, shooter.y - shooter.vy * 0.12);
            ctx.stroke();
          }
        }
      } else if (shooter) {
        shooter = null;
      }

      // 🎆 Layer 3.5b: fireworks bursts (the 'burst' scene kind only)
      if (sc && sc.kind === 'burst') {
        if (bursts.length < 2 && Math.random() < dt / 2.2) {
          bursts.push({
            x: cssW * (0.15 + Math.random() * 0.7),
            y: cssH + 6,
            targetY: cssH * (0.12 + Math.random() * 0.3),
            vy: -(cssH * (0.55 + Math.random() * 0.25)),
            color: sc.style.colors[(Math.random() * sc.style.colors.length) | 0], // eslint-disable-line no-bitwise
            phase: 'rise', sparks: null, age: 0,
          });
        }
        for (let bi = bursts.length - 1; bi >= 0; bi--) {
          const b = bursts[bi];
          if (b.phase === 'rise') {
            b.y += b.vy * dt;
            ctx.globalAlpha = 0.55;
            ctx.fillStyle = b.color;
            ctx.beginPath();
            ctx.arc(b.x, b.y, 1.6, 0, Math.PI * 2);
            ctx.fill();
            if (b.y <= b.targetY) {
              b.phase = 'boom';
              b.sparks = [];
              const n = 22;
              const speed = 60 + Math.random() * 50;
              for (let i = 0; i < n; i++) {
                const a = (i / n) * Math.PI * 2 + Math.random() * 0.2;
                b.sparks.push({ dx: 0, dy: 0, vx: Math.cos(a) * speed * (0.7 + Math.random() * 0.6), vy: Math.sin(a) * speed * (0.7 + Math.random() * 0.6) });
              }
            }
          } else {
            b.age += dt;
            const t = b.age / 1.3;
            if (t >= 1) { bursts.splice(bi, 1); continue; }
            ctx.globalAlpha = 0.5 * (1 - t);
            ctx.fillStyle = b.color;
            for (const sp of b.sparks) {
              sp.vy += 26 * dt; // gravity pulls the bloom down as it fades
              sp.dx += sp.vx * dt;
              sp.dy += sp.vy * dt;
              ctx.fillRect(b.x + sp.dx, b.y + sp.dy, 1.6, 1.6);
            }
          }
        }
      } else if (bursts.length) {
        bursts.length = 0;
      }

      // Layer 4: tap ripples
      const now = performance.now();
      ctx.strokeStyle = (sc && sc.style.ripple) || style.ripple;
      for (let i = 0; i < MAX_RIPPLES; i++) {
        const r = ripples[i];
        if (r.born < 0) continue;
        const age = (now - r.born) / RIPPLE_LIFE_MS;
        if (age >= 1) { r.born = -1; continue; }
        const ease = 1 - (1 - age) * (1 - age); // ease-out expansion
        ctx.globalAlpha = 0.28 * (1 - age);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 8 + ease * 96, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    function tick(ts) {
      if (!running) return;
      const dt = lastTs > 0 ? Math.min(DT_CLAMP_S, (ts - lastTs) / 1000) : 0.016;
      lastTs = ts;
      // Resting cap ~20fps (Nina's ruling 08-14): with no /scene live the
      // base-theme particles are deliberately near-invisible, and a full-rate
      // full-viewport redraw is the largest always-on GPU cost in a space.
      // dt banks in restAccum so motion SPEED is unchanged — only update
      // cadence drops. /scenes keep the full display rate. 0.05s steps are
      // already the physics ceiling (DT_CLAMP_S).
      // 🚪 09-15: a scene stamped resting:true (the /room per-room ambience —
      // roomSceneFallback's RESTING map, near-invisible 'low' looks that can
      // run 24/7 in a parked tab) keeps the resting cadence too; without
      // this, a viewer parked in the library would run the canvas full-rate
      // for hours. Real /scenes and weather looks stay full display rate.
      if (!sceneRef.current || sceneRef.current.resting) {
        restAccum += dt;
        if (restAccum < 0.05) { rafId = window.requestAnimationFrame(tick); return; }
        clock += restAccum;
        drawFrame(restAccum);
        restAccum = 0;
      } else {
        restAccum = 0;
        clock += dt;
        drawFrame(dt);
      }
      rafId = window.requestAnimationFrame(tick);
    }

    function updateRunning() {
      const shouldRun = !disposed && pageVisible && onScreen;
      if (shouldRun === running) return;
      running = shouldRun;
      if (running) {
        lastTs = 0;
        rafId = window.requestAnimationFrame(tick);
      } else {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    // Stop the loop entirely when the tab is hidden.
    const onVisibility = () => {
      pageVisible = !document.hidden;
      updateRunning();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Stop the loop entirely when the canvas is offscreen.
    let io = null;
    if (typeof window.IntersectionObserver === 'function') {
      io = new window.IntersectionObserver((entries) => {
        for (let i = 0; i < entries.length; i++) onScreen = entries[i].isIntersecting;
        updateRunning();
      });
      io.observe(canvas);
    }

    // Debounced resize.
    let resizeTimer = 0;
    let ro = null;
    if (typeof window.ResizeObserver === 'function') {
      ro = new window.ResizeObserver(() => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => { if (!disposed) sizeCanvas(); }, RESIZE_DEBOUNCE_MS);
      });
      ro.observe(wrap);
    }

    // Layer 4 input: canvas is pointerEvents:none, so listen on window and
    // hit-test the tap against the canvas bounds.
    const onPointerDown = (e) => {
      if (!running) return;
      if (fieldRef.current && fieldRef.current.bare) return; // a peek click belongs to the glass
      const now = performance.now();
      if (now - lastTapAt < TAP_THROTTLE_MS) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      lastTapAt = now;
      // free slot, else steal the oldest live ripple
      let slot = -1;
      let oldest = 0;
      let oldestBorn = Infinity;
      for (let i = 0; i < MAX_RIPPLES; i++) {
        if (ripples[i].born < 0) { slot = i; break; }
        if (ripples[i].born < oldestBorn) { oldestBorn = ripples[i].born; oldest = i; }
      }
      if (slot < 0) slot = oldest;
      ripples[slot].x = x;
      ripples[slot].y = y;
      ripples[slot].born = now;
    };
    window.addEventListener('pointerdown', onPointerDown, { passive: true });

    sizeCanvas();
    updateRunning();

    return () => {
      disposed = true;
      running = false;
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(resizeTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', onPointerDown);
      if (io) io.disconnect();
      if (ro) ro.disconnect();
    };
  }, [themeKey, active]);

  if (!active) return null;

  // 🎭 A live scene owns the wash: its tint IS the room's mood, and it must beat
  // the space's accent (which otherwise wins over the theme's own wash).
  const sceneStyle = sceneKey ? SCENE_STYLES[sceneKey] : null;
  const wash = (sceneStyle && sceneStyle.wash)
    || (accent && hexToRgba(accent, 0.05))
    || THEME_STYLES[themeKey].wash;

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        // 🔍 bare (the peek): no tint over a painting, and the picture's own corners
        ...(field && field.bare ? { borderRadius: 8 } : { background: 'radial-gradient(120% 85% at 50% 0%, ' + wash + ' 0%, rgba(0,0,0,0) 70%)' }),
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
